import { CoreLayoutView, CoreTextView, ORIENTATION_ENUM, type TCoreLayoutStyleProps, type TCoreTextStyleProps } from '@core/widgets';
import { TViewCommonOption } from '@libs/types';
import React from 'react';
import { getSemanticColors, memoByPalette, SemanticColor, TSemanticColors } from '../../../theme/colors';
import { TextStyle } from '../../../types/styles';
import styles from './tag.module.css';

type TTagVariant = 'text' | 'tag';
type TTagHierarchy = 'primary' | 'secondary' | 'inverted';
type TTagSize = 'default' | 'small';

type TTagProps = {
    metaText?: string;
    metaVariant?: TTagVariant;
    metaHierarchy?: TTagHierarchy;
    size?: TTagSize;
    /** Design version of the widget it renders for (`TWidget.version`): picks semantic colors. */
    version: number | undefined;
} & Pick<TViewCommonOption, 'styleName' | 'parentStyleNames' | 'isDisabled'>;

/**
 * Color palette of the tag label: the base color and its `disabled` variant in a single object.
 * It is spread into CoreTextView's `styleProps` as a whole, so the core widget — not this
 * component — resolves the active state from its own `isDisabled` prop.
 */
type TTagTextPalette = Pick<TCoreTextStyleProps, 'main' | 'disabled'>;

/** Typography (plus the casing) per variant, shaped as CoreTextView style props. */
const TAG_TYPOGRAPHY_MAP: Record<TTagVariant, TCoreTextStyleProps> = {
    text: TextStyle.main,
    tag: { ...TextStyle.tag, textTransform: 'uppercase' },
};

/** Label palette per variant / hierarchy. `inverted` is a `tag`-only design — as plain text it reads as `primary`. */
const buildTagTextMap = (colors: TSemanticColors): Record<TTagVariant, Record<TTagHierarchy, TTagTextPalette>> => ({
    text: {
        primary: { main: 'var(--black)', disabled: { main: 'var(--grey_40_lc)' } },
        secondary: { main: 'var(--grey_72_lc)', disabled: { main: 'var(--grey_40_lc)' } },
        inverted: { main: 'var(--black)', disabled: { main: 'var(--grey_40_lc)' } },
    },
    tag: {
        primary: { main: 'var(--white_base)', disabled: { main: 'var(--white_40_tr_base)' } },
        secondary: { main: colors[SemanticColor.TextPrimaryOnDarkFill], disabled: { main: colors[SemanticColor.TextPrimary] } },
        inverted: { main: colors[SemanticColor.TextEmphasised], disabled: { main: colors[SemanticColor.TextEmphasised] } },
    },
});
const getTagTextMap = memoByPalette(buildTagTextMap);

/** Pill background per hierarchy — only the `tag` variant draws one, `text` stays transparent. */
const TAG_BACKGROUND_MAP: Record<TTagHierarchy, Record<'main' | 'disabled', string>> = {
    primary: { main: 'var(--purple_100)', disabled: 'var(--purple_16_lc)' },
    secondary: { main: 'var(--grey_40_lc)', disabled: 'var(--grey_16_lc)' },
    inverted: { main: 'var(--white_base)', disabled: 'var(--white_base)' },
};

const TAG_PADDING_MAP: Record<TTagSize, TCoreLayoutStyleProps['padding']> = {
    default: { top: 5, right: 10, bottom: 5, left: 10 },
    small: { top: 3, right: 6, bottom: 3, left: 6 },
};

const TAG_BORDER_RADIUS = 24;

const DEFAULT_VARIANT: TTagVariant = 'text';
const DEFAULT_HIERARCHY: TTagHierarchy = 'primary';
const DEFAULT_SIZE: TTagSize = 'default';

export const Tag = ({ metaVariant = DEFAULT_VARIANT, metaHierarchy = DEFAULT_HIERARCHY, size = DEFAULT_SIZE, ...props }: TTagProps) => {
    const isTagVariant = metaVariant === 'tag';

    // `fit-content` + `fullWidth` keep the pill shrink-wrapped around its label instead of the layout's 100% default.
    const layoutStyleProps: TCoreLayoutStyleProps = {
        orientation: ORIENTATION_ENUM.HORIZONTAL,
        lineX: 'center',
        lineY: 'center',
        width: 'fit-content',
        ...(isTagVariant && { borderRadius: TAG_BORDER_RADIUS, padding: TAG_PADDING_MAP[size] }),
    };

    // The background is applied inline rather than through `styleProps`: CoreLayoutView merges style props under the
    // resolved theme palette, and a tag has no theme node of its own — it falls back to `primary`, whose fill would
    // paint every tag black. The `text` variant needs the inline value too, as `transparent`, to suppress that fallback.
    const background = isTagVariant ? TAG_BACKGROUND_MAP[metaHierarchy][props.isDisabled ? 'disabled' : 'main'] : 'transparent';

    return (
        <CoreLayoutView as="span" fullWidth isDisabled={props.isDisabled} className={styles.tagContainer} styleProps={layoutStyleProps} style={{ background }}>
            <CoreTextView
                text={props.metaText}
                as="p"
                isDisabled={props.isDisabled}
                parentStyleNames={props.parentStyleNames}
                styleName={props.styleName}
                styleProps={{
                    ...TAG_TYPOGRAPHY_MAP[metaVariant],
                    ...getTagTextMap(getSemanticColors(props.version))[metaVariant][metaHierarchy],
                }}
            />
        </CoreLayoutView>
    );
};
